import type { MatchRead, MatchSide } from '@volee/api-client'

// The calendar's two filter levels, kept away from JSX. Why they never call the API — the
// row already carries every field they read — is in apps/frontend/docs/boot-e-le-due-righe.md.

export const MATCH_SCOPES = ['all', 'upcoming', 'played'] as const

export type MatchScope = (typeof MATCH_SCOPES)[number]

export const MATCH_VENUES = ['anywhere', 'home', 'away'] as const

export type MatchVenue = (typeof MATCH_VENUES)[number]

export interface MatchFilters {
  readonly scope: MatchScope
  readonly teamId: number | null
  readonly groupId: number | null
  readonly venue: MatchVenue
  /** An ISO date, `2026-11-01`, and not a date-time: a bound is a day, not an instant. */
  readonly from: string | null
  readonly to: string | null
  readonly withPostponed: boolean
  /** Only the fixtures whose convocazione the reader writes and is not published. Not read by
   *  `filterMatches`, which has no convocazione to look at: the page applies it. */
  readonly toPrepare: boolean
}

/** Every filter off, and the scope on the question most people arrive with. */
export const NO_MATCH_FILTERS: MatchFilters = {
  scope: 'upcoming',
  teamId: null,
  groupId: null,
  venue: 'anywhere',
  from: null,
  to: null,
  withPostponed: false,
  toPrepare: false,
}

export interface MatchFilterOption {
  readonly value: number
  readonly label: string
}

function ourSide(match: MatchRead, clubId: number): MatchSide | null {
  if (match.home.club_id === clubId) return match.home
  if (match.away.club_id === clubId) return match.away
  return null
}

function matchesScope(match: MatchRead, scope: MatchScope, now: string): boolean {
  if (scope === 'all') return true
  // A scheduled fixture nobody marked played is already past, so the date decides as much
  // as the status does.
  const isPast = match.status === 'played' || match.kicks_off_at < now
  return scope === 'played' ? isPast : !isPast
}

function matchesVenue(match: MatchRead, venue: MatchVenue, clubId: number): boolean {
  if (venue === 'anywhere') return true
  const hosting = match.home.club_id === clubId
  return venue === 'home' ? hosting : !hosting
}

function withinPeriod(match: MatchRead, from: string | null, to: string | null): boolean {
  const day = match.kicks_off_at.slice(0, 10)
  if (from !== null && day < from) return false
  return !(to !== null && day > to)
}

export function filterMatches(
  matches: readonly MatchRead[],
  filters: MatchFilters,
  clubId: number,
  now: string,
): readonly MatchRead[] {
  return matches.filter((match) => {
    if (match.status === 'postponed' && !filters.withPostponed) return false
    if (!matchesScope(match, filters.scope, now)) return false
    if (!matchesVenue(match, filters.venue, clubId)) return false
    if (!withinPeriod(match, filters.from, filters.to)) return false
    if (filters.groupId !== null && match.league_group_id !== filters.groupId) return false
    if (filters.teamId === null) return true
    return ourSide(match, clubId)?.team_id === filters.teamId
  })
}

function uniqueBy(options: readonly MatchFilterOption[]): readonly MatchFilterOption[] {
  return [...new Map(options.map((one) => [one.value, one])).values()]
}

/** The squads that actually play, named by their leva: an entry that filters nothing is a
 *  promise the list cannot keep. */
export function teamOptionsOf(
  matches: readonly MatchRead[],
  clubId: number,
): readonly MatchFilterOption[] {
  const named = matches.flatMap((match) => {
    const side = ourSide(match, clubId)
    if (side === null) return []
    return [{ value: side.team_id, label: match.league_category_name }]
  })
  return uniqueBy(named)
}

/** The campionato and the code together: every girone of a province is «A», so the code
 *  alone offers entries nobody can tell apart. */
export function groupOptionsOf(matches: readonly MatchRead[]): readonly MatchFilterOption[] {
  return uniqueBy(
    matches.map((match) => ({
      value: match.league_group_id,
      label: `${match.league_category_name} — girone ${match.league_group_code}`,
    })),
  )
}
